import { formatLocalDate } from '../../utils/localDateTime';
import React, { useState, useCallback, useRef } from 'react';
import { printRenderedReport } from '../../utils/printRenderedReport';

// ── Types ─────────────────────────────────────────────────────────────────────
export type BriefType = 'riskMatrix' | 'political' | 'keyFigures' | 'threatGroups' | 'threatGroupDetail';

interface SectionBriefModalProps {
    briefType: BriefType;
    data: any;
    result: any;
    onClose: () => void;
    getThreatGroupColor: (type: string) => string;
    detailGroup?: any;
    onNavigate?: (params: any) => void;
}

// ── Helpers ──────────────────────────────────────────────────────────────────
const today = formatLocalDate(new Date());

const PoliticalAssessment: React.FC<{ value: unknown }> = ({ value }) => {
    if (typeof value === 'string')
        return <>{value}</>;
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return null;
    const { description, sources, accessEndpoints } = value as Record<string, unknown>;
    return (
        <>
            {typeof description === 'string' && <p>{description}</p>}
            {Array.isArray(sources) && (
                <ul className="mt-3 space-y-1 text-xs">
                    {sources.map((source, index) => {
                        if (typeof source !== 'string')
                            return null;
                        const endpoint = Array.isArray(accessEndpoints) ? accessEndpoints[index] : undefined;
                        return (
                            <li key={index}>
                                {typeof endpoint === 'string' && /^https?:\/\//i.test(endpoint)
                                    ? <a href={endpoint} target="_blank" rel="noopener noreferrer" className="underline">{source}</a>
                                    : source}
                            </li>
                        );
                    })}
                </ul>
            )}
        </>
    );
};

const RISK_COLOR = (score: number) => {
    if (score >= 80) return { bg: 'bg-red-600',    text: 'text-white',      label: 'CRITICAL',   bar: '#dc2626' };
    if (score >= 60) return { bg: 'bg-orange-500', text: 'text-white',      label: 'VERY HIGH',  bar: '#f97316' };
    if (score >= 40) return { bg: 'bg-amber-500',  text: 'text-white',      label: 'HIGH',       bar: '#f59e0b' };
    if (score >= 20) return { bg: 'bg-yellow-400', text: 'text-slate-900',  label: 'MODERATE',   bar: '#eab308' };
    return              { bg: 'bg-green-500',  text: 'text-white',      label: 'LOW',        bar: '#22c55e' };
};

// ── Shared Brief Header ───────────────────────────────────────────────────────
const BriefHeader: React.FC<{ title: string; subtitle: string; country: string }> = ({ title, subtitle, country }) => (
    <div className="bg-slate-900 text-white px-8 py-6">
        <div className="flex items-start justify-between">
            <div>
                <div className="flex items-center gap-3 mb-1">
                    <div className="text-blue-400 font-black text-xs uppercase tracking-widest">IntelOptics ECC</div>
                    <div className="h-3 w-px bg-slate-600" />
                    <div className="text-[9px] font-black text-slate-500 uppercase tracking-widest">Country Intelligence Platform</div>
                </div>
                <h1 className="text-2xl font-black leading-tight text-white">{title}</h1>
                <div className="text-sm text-slate-400 mt-1 font-medium">{subtitle} &mdash; {country}</div>
            </div>
            <div className="text-right shrink-0 ml-4">
                <div className="inline-flex items-center gap-1.5 bg-red-700/30 border border-red-700/50 px-3 py-1 rounded text-[9px] font-black text-red-300 uppercase tracking-widest">
                    &#9873; INTERNAL / SENSITIVE
                </div>
                <div className="text-[9px] text-slate-500 mt-2 font-bold">Generated: {today}</div>
            </div>
        </div>
    </div>
);

// ── Brief Section Wrapper ─────────────────────────────────────────────────────
const BSection: React.FC<{ title: string; children: React.ReactNode; borderColor?: string }> = ({ title, children, borderColor = '#3b82f6' }) => (
    <div className="mb-8">
        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-200">
            <div className="w-1 h-5 rounded-full" style={{ backgroundColor: borderColor }} />
            <h2 className="text-xs font-black text-slate-600 uppercase tracking-widest">{title}</h2>
        </div>
        {children}
    </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// BRIEF 1: RISK MATRIX
// ─────────────────────────────────────────────────────────────────────────────
const RiskMatrixBrief: React.FC<{ data: any; result: any }> = ({ data, result }) => {
    const riskFactors: any[] = data.riskFactors || [];
    const overallScore = Number(data.overallGeopoliticalRiskScore ?? result?.overallGeopoliticalRiskScore ?? 0);
    const overall = RISK_COLOR(overallScore);

    return (
        <div className="px-8 py-6 bg-white">
            {/* Overall Risk */}
            <div className={`flex items-center gap-4 p-5 rounded-xl mb-6 shadow-lg ${overall.bg} ${overall.text}`}>
                <div>
                    <div className="text-[9px] font-black uppercase tracking-widest opacity-75 mb-0.5">Overall Geopolitical Risk Score</div>
                    <div className="text-4xl font-black">{overallScore}<span className="text-xl font-bold opacity-70">/100</span></div>
                </div>
                <div className="ml-auto text-right">
                    <div className="text-3xl font-black">{overall.label}</div>
                    <div className="text-xs opacity-75">{data.country || result?.country}</div>
                </div>
            </div>

            {/* OSINT Methodology Disclaimer */}
            <BSection title="OSINT Methodology & Verification Notice" borderColor="#f59e0b">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-xs text-amber-900 space-y-3 leading-relaxed">
                    <p><strong>Data Sources:</strong> Risk scores are generated using open-source intelligence (OSINT) aggregated from the US State Department Travel Advisories (RSS), World Bank Governance Indicators (WGI), WHO country health data, ACAPS humanitarian situation reports, OpenAlex and Semantic Scholar academic databases, Library of Congress country studies, and real-time open-source news media.</p>
                    <p><strong>AI Analysis Engine:</strong> Scoring uses the configured Google Gemini model. The model synthesises the above OSINT sources and applies a structured geopolitical risk methodology. Outputs are AI-generated estimates and are not produced by human intelligence officers.</p>
                    <p><strong>Scoring Methodology:</strong> Risk factors are scored on a normalised 0&ndash;100 scale (0 = no risk, 100 = catastrophic). The overall score is a weighted composite of eight factor dimensions. Scores are calibrated against US State Department advisory levels where available (Level 1 = max 20, Level 2 = max 40, Level 4 = min 76).</p>
                    <p><strong>Verification Limitations:</strong> OSINT is subject to information gaps, source bias, and time-lag. This analysis reflects conditions <em>at the time of report generation only</em> and may not capture rapidly developing situations. All high-stakes security decisions must be verified through primary intelligence channels and qualified human subject-matter experts.</p>
                    <p className="font-bold text-amber-800">&#9888; This report is for strategic awareness and risk planning only. It does not constitute professional security advice.</p>
                </div>
            </BSection>

            {/* Risk Factors */}
            <BSection title="Risk Factor Breakdown" borderColor="#64748b">
                {riskFactors.length > 0 ? (
                    <div className="space-y-3">
                        {riskFactors.map((rf: any, i: number) => {
                            const c = RISK_COLOR(Number(rf.score ?? 0));
                            return (
                                <div key={i} className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-sm font-bold text-slate-800">{rf.factor}</span>
                                        <div className="flex items-center gap-2">
                                            <span className={`text-[9px] font-black px-2 py-0.5 rounded uppercase ${c.bg} ${c.text}`}>{c.label}</span>
                                            <span className="text-sm font-black text-slate-700">{rf.score}/100</span>
                                        </div>
                                    </div>
                                    <div className="w-full bg-slate-200 rounded-full h-2">
                                        <div className="h-2 rounded-full" style={{ width: `${rf.score}%`, backgroundColor: c.bar }} />
                                    </div>
                                    {rf.description && <p className="text-xs text-slate-500 mt-2 leading-relaxed italic">{rf.description}</p>}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-8 text-slate-400 text-sm italic">No risk factor data available.</div>
                )}
            </BSection>

            {/* Analyst Assessment */}
            {(data.executiveSummary || data.summary) && (
                <BSection title="Analyst Assessment" borderColor="#3b82f6">
                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 text-sm text-blue-900 leading-relaxed italic">
                        {data.executiveSummary || data.summary}
                    </div>
                </BSection>
            )}

            {/* Sources */}
            {data.sources && data.sources.filter((s: any) => s.name || s.sourceName).length > 0 && (
                <BSection title="Intelligence Sources" borderColor="#94a3b8">
                    <div className="overflow-x-auto border border-slate-200 rounded-lg">
                        <table className="min-w-full text-xs">
                            <thead className="bg-slate-100 text-slate-600 font-black uppercase">
                                <tr>
                                    <th className="px-4 py-2 text-left border-b">Area</th>
                                    <th className="px-4 py-2 text-left border-b">Source</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {data.sources.slice(0, 15).map((src: any, i: number) => (
                                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                        <td className="px-4 py-2 font-bold text-slate-700">{src.area || src.assessmentArea || 'General'}</td>
                                        <td className="px-4 py-2 text-slate-500">{src.name || src.sourceName || src.title || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </BSection>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// BRIEF 2: POLITICAL LANDSCAPE
// ─────────────────────────────────────────────────────────────────────────────
const PoliticalLandscapeBrief: React.FC<{ data: any; result: any }> = ({ data, result }) => {
    const parties: any[] = data.politicalParties || [];
    const lo = data.leadershipOverview;
    const ga = data.governmentApparatus || data.governmentStructure;

    return (
        <div className="px-8 py-6 bg-white">
            {/* Political Overview */}
            {lo && (
                <BSection title="Political Overview" borderColor="#6366f1">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                        {[
                            { label: 'Government Type', value: lo.governmentType },
                            { label: 'Head of State', value: lo.headOfState },
                            { label: 'Political Structure', value: lo.politicalStructure },
                            { label: 'Electoral System', value: lo.electoralSystem },
                            { label: 'Leadership Status', value: lo.currentStatus },
                            { label: 'Stability Outlook', value: lo.stabilityOutlook },
                        ].filter(i => i.value).map(({ label, value }) => (
                            <div key={label} className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                                <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">{label}</div>
                                <div className="text-sm font-bold text-slate-800">{value}</div>
                            </div>
                        ))}
                    </div>
                    {lo.politicalNarrative && (
                        <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-5 text-sm text-indigo-900 leading-relaxed italic">{lo.politicalNarrative}</div>
                    )}
                </BSection>
            )}

            {/* Political Parties */}
            {parties.length > 0 && (
                <BSection title="Political Parties & Factions" borderColor="#3b82f6">
                    <div className="overflow-x-auto border border-slate-200 rounded-xl shadow-sm">
                        <table className="min-w-full text-sm text-left">
                            <thead className="bg-slate-900 text-white text-[10px] font-black uppercase tracking-widest">
                                <tr>
                                    <th className="px-4 py-3">Party</th>
                                    <th className="px-4 py-3">Ideology / Stance</th>
                                    <th className="px-4 py-3">Leader</th>
                                    <th className="px-4 py-3 text-center">Seats</th>
                                    <th className="px-4 py-3 text-right">Influence</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {parties.map((p: any, i: number) => (
                                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                        <td className="px-4 py-3 font-black text-slate-900">{p.name}</td>
                                        <td className="px-4 py-3 text-slate-600">{p.orientation || p.stance || p.ideology || '—'}</td>
                                        <td className="px-4 py-3 text-slate-600">{p.leader || '—'}</td>
                                        <td className="px-4 py-3 text-center font-bold text-slate-700">{p.seats || p.parliamentarySeats || '—'}</td>
                                        <td className="px-4 py-3 text-right">
                                            <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${
                                                (p.influenceLevel||p.influence||'').toLowerCase()==='high' ? 'bg-red-100 text-red-700' :
                                                (p.influenceLevel||p.influence||'').toLowerCase().includes('med') ? 'bg-orange-100 text-orange-700' :
                                                'bg-blue-100 text-blue-700'
                                            }`}>{p.influenceLevel || p.influence || 'N/A'}</span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </BSection>
            )}

            {/* Government Apparatus */}
            {ga && (
                <BSection title="Government Structure" borderColor="#64748b">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {['executive','legislative','judicial','intelligence','military'].map(branch => {
                            const bd = ga[branch];
                            if (!bd) return null;
                            const text = typeof bd === 'string' ? bd : bd.description || bd.summary || JSON.stringify(bd);
                            return (
                                <div key={branch} className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                                    <div className="text-[9px] font-black text-blue-600 uppercase tracking-widest mb-2 capitalize">{branch}</div>
                                    <p className="text-sm text-slate-700 leading-relaxed">{text}</p>
                                </div>
                            );
                        })}
                    </div>
                </BSection>
            )}

            {/* Political Risk */}
            {data.pmesii?.political && (
                <BSection title="Political Risk Assessment" borderColor="#ef4444">
                    <div className="bg-red-50 border border-red-100 rounded-xl p-5 text-sm text-red-900 leading-relaxed"><PoliticalAssessment value={data.pmesii.political} /></div>
                </BSection>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// BRIEF 3: KEY POLITICAL FIGURES
// ─────────────────────────────────────────────────────────────────────────────
const KeyFiguresBrief: React.FC<{ data: any; result: any }> = ({ data, result }) => {
    const lo = data.leadershipOverview;
    const keyFigures: any[] = lo?.keyFigures || lo?.keyLeaders || data.keyPoliticalFigures || data.keyFigures || [];
    const headOfState = lo?.headOfState;

    return (
        <div className="px-8 py-6 bg-white">
            {!headOfState && keyFigures.length === 0 && (
                <div className="text-center py-12 text-slate-400">
                    <div className="text-4xl mb-3">&#128100;</div>
                    <div className="text-sm font-bold">Leadership profile data not available.</div>
                    <div className="text-xs mt-1">Run a fresh Deep Dive analysis to generate detailed leadership dossiers.</div>
                </div>
            )}

            {/* National Leadership from leadershipOverview */}
            {lo && (
                <BSection title="National Leadership" borderColor="#3b82f6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        {[
                            { role: 'Head of State', name: lo.headOfState, bio: lo.headOfStateBio },
                            { role: 'Head of Government', name: lo.headOfGovernment, bio: lo.headOfGovernmentBio },
                            { role: 'Defence Minister', name: lo.defenceMinister, bio: null },
                            { role: 'Foreign Minister', name: lo.foreignMinister, bio: null },
                        ].filter(f => f.name).map(({ role, name, bio }) => (
                            <div key={role} className="bg-slate-900 text-white rounded-xl p-4 border border-slate-700">
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-full bg-blue-700 border-2 border-blue-500 flex items-center justify-center font-black text-lg shrink-0">
                                        {(name||'?').charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <div className="text-[9px] font-black text-blue-400 uppercase tracking-widest">{role}</div>
                                        <div className="text-sm font-black text-white">{name}</div>
                                        {bio && <p className="text-xs text-slate-400 mt-1 leading-relaxed">{bio}</p>}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {lo.coalitionDynamics && (
                        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-900 leading-relaxed">
                            <div className="text-[9px] font-black text-blue-500 uppercase tracking-widest mb-1">Coalition & Political Dynamics</div>
                            {lo.coalitionDynamics}
                        </div>
                    )}
                </BSection>
            )}

            {/* Full Key Figures Dossier */}
            {keyFigures.length > 0 && (
                <BSection title="Key Political Figures Dossier" borderColor="#6366f1">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {keyFigures.map((fig: any, i: number) => (
                            <div key={i} className="bg-white border-2 border-slate-100 rounded-xl p-5 shadow-sm">
                                <div className="flex items-start gap-3 mb-3">
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-700 to-indigo-800 flex items-center justify-center font-black text-xl text-white border-2 border-blue-300/30 shrink-0">
                                        {(fig.name||'?').charAt(0).toUpperCase()}
                                    </div>
                                    <div className="flex-grow min-w-0">
                                        <div className="text-sm font-black text-slate-900">{fig.name}</div>
                                        <div className="text-[9px] font-black text-blue-500 uppercase tracking-wider">{fig.role || fig.title}</div>
                                        {(fig.branch || fig.party) && <div className="text-[9px] text-slate-400 font-bold">{fig.branch}{fig.party ? ` • ${fig.party}` : ''}</div>}
                                    </div>
                                    {(fig.influenceLevel || fig.influence) && (
                                        <span className={`ml-auto text-[9px] font-black px-2 py-0.5 rounded uppercase shrink-0 ${
                                            ['high','critical'].includes((fig.influenceLevel||fig.influence||'').toLowerCase())
                                                ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
                                        }`}>{fig.influenceLevel || fig.influence}</span>
                                    )}
                                </div>
                                {(fig.bio || fig.description || fig.profile) && (
                                    <p className="text-xs text-slate-600 leading-relaxed border-t border-slate-100 pt-3">{fig.bio || fig.description || fig.profile}</p>
                                )}
                                {(fig.keyPolicies || fig.policies) && (
                                    <div className="mt-2">
                                        <div className="text-[8px] font-black text-indigo-400 uppercase tracking-widest mb-1">Key Policy Positions</div>
                                        <p className="text-[10px] text-slate-500 leading-tight">{fig.keyPolicies || fig.policies}</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </BSection>
            )}

            {data.pmesii?.political && (
                <BSection title="Political Dynamics Assessment" borderColor="#64748b">
                    <div className="bg-slate-50 rounded-xl p-5 border border-slate-200 text-sm text-slate-700 leading-relaxed"><PoliticalAssessment value={data.pmesii.political} /></div>
                </BSection>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// BRIEF 4: THREAT GROUPS OVERVIEW
// ─────────────────────────────────────────────────────────────────────────────
const ThreatGroupsOverviewBrief: React.FC<{
    data: any; result: any;
    getThreatGroupColor: (t: string) => string;
    onSelectGroup: (g: any) => void;
}> = ({ data, result, getThreatGroupColor, onSelectGroup }) => {
    const groups: any[] = data.threatGroups || [];
    const country = data.country || result?.country || 'Unknown';
    const typeCount = groups.reduce((acc: Record<string,number>, g: any) => {
        const type = (g.type || 'Other').split('/')[0].trim();
        acc[type] = (acc[type] || 0) + 1;
        return acc;
    }, {});

    return (
        <div className="px-8 py-6 bg-white">
            {/* Threat Landscape Summary */}
            <BSection title="Threat Landscape Overview" borderColor="#ef4444">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                    <div className="bg-slate-900 rounded-xl p-4 text-center text-white border border-slate-700">
                        <div className="text-3xl font-black text-red-400">{groups.length}</div>
                        <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mt-1">Active Groups</div>
                    </div>
                    {Object.entries(typeCount).slice(0,3).map(([type, count]) => {
                        const color = getThreatGroupColor(type);
                        return (
                            <div key={type} className="rounded-xl p-4 text-center border" style={{ backgroundColor: `${color}15`, borderColor: `${color}40` }}>
                                <div className="text-3xl font-black" style={{ color }}>{count as number}</div>
                                <div className="text-[9px] font-black text-slate-500 uppercase tracking-widest mt-1">{type}</div>
                            </div>
                        );
                    })}
                </div>
                <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-sm text-red-900 leading-relaxed">
                    <strong>Threat Environment:</strong> {country} hosts {groups.length} identified threat actor{groups.length !== 1 ? 's' : ''} across {Object.keys(typeCount).length} operational categories. Click any group below to view the full intelligence dossier.
                </div>
            </BSection>

            {/* OSINT Notice */}
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800 flex gap-3">
                <svg className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <p><strong>OSINT Notice:</strong> Threat actor intelligence is synthesised from open-source reporting, academic research, and routed AI analysis. Group structures, leadership, and operational posture may change rapidly. Verify through primary intelligence channels before operational use.</p>
            </div>

            {/* Group Cards */}
            <BSection title="Identified Threat Actors" borderColor="#64748b">
                <div className="grid grid-cols-1 gap-4">
                    {groups.map((group: any, idx: number) => {
                        const color = getThreatGroupColor(group.type);
                        return (
                            <div key={group.name || idx}
                                className="bg-white border-2 rounded-xl p-5 shadow-sm cursor-pointer hover:shadow-md transition-all"
                                style={{ borderColor: `${color}40` }}
                                onClick={() => onSelectGroup(group)}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-3 flex-grow min-w-0">
                                        <div className="w-3 h-3 rounded-full mt-1.5 shrink-0 border-2 border-white shadow" style={{ backgroundColor: color }} />
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <h3 className="text-sm font-black text-slate-900">{group.name}</h3>
                                                <span className="text-[9px] font-black px-2 py-0.5 rounded uppercase border"
                                                    style={{ backgroundColor: `${color}15`, color, borderColor: `${color}40` }}>{group.type}</span>
                                                {group.threatLevel && (
                                                    <span className={`text-[9px] font-black px-2 py-0.5 rounded uppercase ${
                                                        group.threatLevel==='Critical'?'bg-red-100 text-red-700':
                                                        group.threatLevel==='High'?'bg-orange-100 text-orange-700':'bg-yellow-100 text-yellow-700'
                                                    }`}>{group.threatLevel} Threat</span>
                                                )}
                                            </div>
                                            <p className="text-xs text-slate-500 mt-1.5 leading-relaxed line-clamp-2">{group.summary}</p>
                                            {group.areaOfOperations && (
                                                <div className="text-[9px] text-slate-400 mt-1 font-bold">
                                                    &#128205; {typeof group.areaOfOperations==='string' ? group.areaOfOperations : group.areaOfOperations.description}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="shrink-0 text-[10px] font-black text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100 hover:bg-blue-100 transition-colors whitespace-nowrap">
                                        View Dossier &#8594;
                                    </div>
                                </div>
                                <div className="flex gap-4 mt-3 pt-3 border-t border-slate-100">
                                    {group.keyLeaders?.length > 0 && <div className="text-[9px] text-slate-500"><strong className="text-slate-700">{group.keyLeaders.length}</strong> Leader{group.keyLeaders.length!==1?'s':''}</div>}
                                    {group.recentActivities?.length > 0 && <div className="text-[9px] text-slate-500"><strong className="text-slate-700">{group.recentActivities.length}</strong> Activities</div>}
                                    {group.pmesiiAnalysis && <div className="text-[9px] text-slate-500">&#10003; PMESII</div>}
                                    {group.carverAssessment && <div className="text-[9px] text-slate-500">&#10003; CARVER</div>}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </BSection>
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// BRIEF 5: INDIVIDUAL THREAT GROUP DOSSIER
// ─────────────────────────────────────────────────────────────────────────────
const ThreatGroupDetailBrief: React.FC<{
    group: any; getThreatGroupColor: (t: string) => string; onNavigate?: (p: any) => void;
}> = ({ group, getThreatGroupColor, onNavigate }) => {
    const color = getThreatGroupColor(group.type);

    return (
        <div className="px-8 py-6 bg-white">
            {/* Group Identity Banner */}
            <div className="rounded-xl p-5 mb-6 border-l-4 shadow-sm" style={{ borderLeftColor: color, backgroundColor: `${color}08` }}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                        <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Threat Actor Intelligence Dossier</div>
                        <h2 className="text-xl font-black text-slate-900">{group.name}</h2>
                        <div className="flex gap-2 mt-1.5 flex-wrap">
                            <span className="text-[9px] font-black px-2 py-0.5 rounded uppercase border"
                                style={{ backgroundColor: `${color}15`, color, borderColor: `${color}40` }}>{group.type}</span>
                            {group.threatLevel && (
                                <span className={`text-[9px] font-black px-2 py-0.5 rounded uppercase ${
                                    group.threatLevel==='Critical'?'bg-red-100 text-red-700':
                                    group.threatLevel==='High'?'bg-orange-100 text-orange-700':'bg-yellow-100 text-yellow-700'
                                }`}>{group.threatLevel} Threat Level</span>
                            )}
                        </div>
                    </div>
                    {onNavigate && (
                        <button onClick={() => onNavigate({ page: 'gtg_dashboard', params: { initialSearch: group.name } })}
                            className="text-[10px] font-black uppercase text-blue-600 hover:text-blue-700 bg-blue-50 border border-blue-200 px-4 py-2 rounded-lg transition-colors">
                            Open OSINT Data Hub &#8594;
                        </button>
                    )}
                </div>
            </div>

            {group.summary && (
                <BSection title="Overview" borderColor="#3b82f6">
                    <p className="text-sm text-slate-700 leading-relaxed">{group.summary}</p>
                </BSection>
            )}
            {group.historicalContext && (
                <BSection title="Historical Context & Origin" borderColor="#6366f1">
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 text-sm text-slate-700 leading-relaxed">{group.historicalContext}</div>
                </BSection>
            )}
            {group.currentStructure && (
                <BSection title="Current Structure & Strength" borderColor="#f59e0b">
                    <div className="bg-amber-50 border border-amber-100 rounded-xl p-5 text-sm text-amber-900 leading-relaxed">{group.currentStructure}</div>
                </BSection>
            )}
            {group.keyLeaders && group.keyLeaders.length > 0 && (
                <BSection title="Current Leadership" borderColor="#10b981">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {group.keyLeaders.map((leader: any, i: number) => (
                            <div key={i} className="bg-white border-2 border-slate-100 rounded-xl p-4 shadow-sm">
                                <div className="flex items-start gap-3">
                                    <div className="w-10 h-10 rounded-full flex items-center justify-center font-black text-lg text-white shrink-0" style={{ backgroundColor: color }}>
                                        {(leader.name||'?').charAt(0).toUpperCase()}
                                    </div>
                                    <div>
                                        <div className="text-sm font-black text-slate-900">{leader.name}</div>
                                        <div className="text-[9px] font-black text-emerald-600 uppercase tracking-wider">{leader.role}</div>
                                        {leader.bio && <p className="text-xs text-slate-500 mt-1 leading-relaxed">{leader.bio}</p>}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </BSection>
            )}
            {group.areaOfOperations && (
                <BSection title="Area of Operations" borderColor="#ef4444">
                    <div className="bg-red-50 border border-red-100 rounded-xl p-5 text-sm text-red-900 leading-relaxed">
                        {typeof group.areaOfOperations==='string' ? group.areaOfOperations : group.areaOfOperations.description || 'See map for geographic coverage.'}
                    </div>
                    {group.areaOfOperations?.locations?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                            {group.areaOfOperations.locations.map((loc: any, i: number) => (
                                <span key={i} className="text-[9px] font-black px-2 py-1 bg-red-100 text-red-700 rounded uppercase tracking-wider">
                                    &#128205; {loc.name || `${loc.lat?.toFixed(2)}, ${loc.lng?.toFixed(2)}`}
                                </span>
                            ))}
                        </div>
                    )}
                </BSection>
            )}
            {group.recentActivities && group.recentActivities.length > 0 && (
                <BSection title="Recent Activity Timeline" borderColor="#f97316">
                    <div className="space-y-3">
                        {group.recentActivities.map((act: any, i: number) => (
                            <div key={i} className="flex gap-4 p-4 bg-white border border-slate-100 rounded-xl shadow-sm border-l-4" style={{ borderLeftColor: color }}>
                                <div className="text-[9px] font-black text-orange-500 pt-0.5 min-w-[68px] whitespace-nowrap">{formatLocalDate(act.date, 'RECENT')}</div>
                                <div>
                                    <div className="text-xs font-black text-slate-800 uppercase tracking-wider">{act.type}</div>
                                    <p className="text-xs text-slate-600 mt-0.5 leading-relaxed">{act.description}</p>
                                    {act.lat && act.lng && <div className="text-[9px] text-slate-400 mt-1">&#128205; {act.lat.toFixed(4)}&deg;, {act.lng.toFixed(4)}&deg;</div>}
                                </div>
                            </div>
                        ))}
                    </div>
                </BSection>
            )}
            {group.pmesiiAnalysis && (
                <BSection title="PMESII Analysis" borderColor="#a855f7">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(group.pmesiiAnalysis).map(([key, val]) => (
                            <div key={key} className="bg-purple-50 border border-purple-100 rounded-xl p-4">
                                <div className="text-[9px] font-black text-purple-600 uppercase tracking-widest mb-1.5 capitalize">{key}</div>
                                <p className="text-sm text-purple-900 leading-relaxed">{typeof val==='string'?val:JSON.stringify(val)}</p>
                            </div>
                        ))}
                    </div>
                </BSection>
            )}
            {group.carverAssessment && (
                <BSection title="CARVER Target Assessment" borderColor="#ec4899">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
                        {['criticality','accessibility','recuperability','vulnerability','effect','recognizability'].map(key => (
                            <div key={key} className="bg-pink-50 border border-pink-100 rounded-xl p-4">
                                <div className="text-[9px] font-black text-pink-500 uppercase tracking-widest mb-1 capitalize">{key}</div>
                                <p className="text-sm font-bold text-pink-900">{(group.carverAssessment as any)[key] || 'N/A'}</p>
                            </div>
                        ))}
                    </div>
                    {group.carverAssessment.overallAssessment && (
                        <div className="bg-pink-50 border border-pink-200 rounded-xl p-5 text-sm text-pink-900 italic leading-relaxed">
                            <strong>Overall Assessment:</strong> {group.carverAssessment.overallAssessment}
                        </div>
                    )}
                </BSection>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN MODAL
// ─────────────────────────────────────────────────────────────────────────────
export const SectionBriefModal: React.FC<SectionBriefModalProps> = ({
    briefType: initialBriefType, data, result, onClose, getThreatGroupColor, detailGroup: initialDetailGroup, onNavigate,
}) => {
    const [briefType, setBriefType] = useState<BriefType>(initialBriefType);
    const printAreaRef = useRef<HTMLDivElement>(null);
    const [detailGroup, setDetailGroup] = useState<any>(initialDetailGroup || null);

    const handleSelectGroup = useCallback((group: any) => {
        setDetailGroup(group);
        setBriefType('threatGroupDetail');
    }, []);

    const handleBackToOverview = useCallback(() => {
        setDetailGroup(null);
        setBriefType('threatGroups');
    }, []);

    const country = data.country || result?.country || 'Unknown Country';

    const BRIEF_META: Record<BriefType, { title: string; subtitle: string }> = {
        riskMatrix:       { title: 'Integrated Geopolitical Risk Matrix',    subtitle: 'Full Risk Factor Analysis & OSINT Methodology' },
        political:        { title: 'Political Landscape Assessment',          subtitle: 'Political Parties, Government Structure & Dynamics' },
        keyFigures:       { title: 'Key Political Figures Dossier',           subtitle: 'National Leadership & Influence Assessment' },
        threatGroups:     { title: 'Threat Group Intelligence Assessment',    subtitle: 'Active Threat Actors & Operational Analysis' },
        threatGroupDetail:{ title: detailGroup?.name || 'Threat Actor Dossier', subtitle: `${detailGroup?.type||''} \u2014 ${detailGroup?.threatLevel||''} Threat Level` },
    };

    const meta = BRIEF_META[briefType];

    return (
        <>
            <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm overflow-y-auto py-6 px-4 flex items-start justify-center cursor-pointer"
                onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
                <div ref={printAreaRef} id="sbm-print-area" className="analysis-report-print-area bg-white w-full max-w-5xl rounded-2xl shadow-2xl overflow-hidden my-4 cursor-default">

                    {/* Controls */}
                    <div id="sbm-controls" data-print-exclude className="bg-slate-800 px-4 py-2.5 flex items-center justify-between gap-3 border-b border-slate-700 sticky top-0 z-10">
                        <div className="flex items-center gap-2">
                            {briefType === 'threatGroupDetail' && (
                                <button onClick={handleBackToOverview}
                                    className="flex items-center gap-1.5 text-[10px] font-black text-slate-400 hover:text-white uppercase tracking-wider px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 transition-colors">
                                    &#8592; Back to All Groups
                                </button>
                            )}
                            <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest hidden sm:block">
                                Section Brief &mdash; {country}
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => printRenderedReport(`${meta.title} — ${country}`, printAreaRef.current)}
                                className="flex items-center gap-1.5 text-[10px] font-black text-blue-300 hover:text-white uppercase tracking-wider px-3 py-1.5 rounded bg-blue-900/50 hover:bg-blue-800 border border-blue-700/50 transition-colors">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                                Download / Print PDF
                            </button>
                            <button onClick={onClose}
                                className="flex items-center gap-1 text-[10px] font-black text-slate-400 hover:text-white uppercase tracking-wider px-3 py-1.5 rounded bg-slate-700 hover:bg-slate-600 transition-colors">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                Close
                            </button>
                        </div>
                    </div>

                    {/* Header */}
                    <BriefHeader title={meta.title} subtitle={meta.subtitle} country={country} />

                    {/* Content */}
                    {briefType === 'riskMatrix' && <RiskMatrixBrief data={data} result={result} />}
                    {briefType === 'political' && <PoliticalLandscapeBrief data={data} result={result} />}
                    {briefType === 'keyFigures' && <KeyFiguresBrief data={data} result={result} />}
                    {briefType === 'threatGroups' && (
                        <ThreatGroupsOverviewBrief data={data} result={result} getThreatGroupColor={getThreatGroupColor} onSelectGroup={handleSelectGroup} />
                    )}
                    {briefType === 'threatGroupDetail' && detailGroup && (
                        <ThreatGroupDetailBrief group={detailGroup} getThreatGroupColor={getThreatGroupColor} onNavigate={onNavigate} />
                    )}

                    {/* Footer */}
                    <div className="px-8 py-4 bg-slate-50 border-t border-slate-200 flex justify-between items-center text-[9px] text-slate-400 font-bold uppercase tracking-widest">
                        <div>IntelOptics ECC &mdash; Country Intelligence Platform</div>
                        <div>Generated: {today} | Internal / Sensitive</div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SectionBriefModal;
